import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  DatePicker,
  Select,
  Typography,
  message,
} from "antd";
import { ColumnsType } from "antd/es/table";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BASE_API_URL } from "../../../utils/consts";
import { isSuperAdmin } from "../../../utils/permissions";
import { isLocationActive } from "../../../utils/helpers/locationVisibility";

type ClientRecord = {
  _id: string;
  name?: string;
  clientName?: string;
  maxUserNumber?: number;
  currency?: string;
  currencyIcon?: string;
  settings?: {
    enabledFeatures?: string[];
    type?: string;
  };
};

type UserRecord = {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  roles?: Array<{ name?: string } | string>;
};

const { Paragraph, Text } = Typography;

const Clients: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const [form] = Form.useForm();
  const [userForm] = Form.useForm();
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [workingPatterns, setWorkingPatterns] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientRecord | null>(null);

  const [superAdminModalOpen, setSuperAdminModalOpen] = useState(false);
  const [clientUsers, setClientUsers] = useState<UserRecord[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [assigningRole, setAssigningRole] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_API_URL}/client/all`);
      setClients(data || []);
    } catch (error) {
      console.error(error);
      message.error(t("Failed to load clients"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isSuperAdmin(userData?.roles)) {
      fetchClients();
    }
  }, [fetchClients, userData?.roles]);

  const loadClientMeta = useCallback(async (clientId?: string) => {
    if (!clientId) return;
    try {
      const [wpRes, locRes] = await Promise.all([
        axios
          .get(`${BASE_API_URL}/${clientId}/working-patterns`)
          .catch(() => ({ data: [] })),
        axios.get(`${BASE_API_URL}/${clientId}/locations`).catch(() => ({
          data: [],
        })),
      ]);
      setWorkingPatterns(wpRes.data || []);
      setLocations((locRes.data || []).filter(isLocationActive));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const onFinish = async (values: any) => {
    setCreating(true);
    try {
      const payload = {
        name: values.name,
        clientName: values.clientName,
        maxUserNumber: values.maxUserNumber,
        currency: values.currency,
        currencyIcon: values.currencyIcon,
      };
      await axios.post(`${BASE_API_URL}/client/create`, payload);
      message.success(t("Client created"));
      form.resetFields();
      fetchClients();
    } catch (error) {
      console.error(error);
      message.error(t("Unable to create client"));
    } finally {
      setCreating(false);
    }
  };

  const openUserModal = useCallback(
    async (client: ClientRecord) => {
      setSelectedClient(client);
      setUserModalOpen(true);
      userForm.resetFields();
      await loadClientMeta(client._id);
    },
    [loadClientMeta, userForm]
  );

  const openSuperAdminModal = useCallback(async (client: ClientRecord) => {
    setSelectedClient(client);
    setSelectedUserId(undefined);
    setSuperAdminModalOpen(true);
    setLoadingUsers(true);
    try {
      const { data } = await axios.get(`${BASE_API_URL}/${client._id}/users`);
      setClientUsers(data || []);
    } catch (error) {
      console.error(error);
      message.error(t("Failed to load users"));
    } finally {
      setLoadingUsers(false);
    }
  }, [t]);

  const assignSuperAdmin = async () => {
    if (!selectedUserId) {
      message.warning(t("Please select a user"));
      return;
    }
    setAssigningRole(true);
    try {
      await axios.put(`${BASE_API_URL}/user/addRole/${selectedUserId}`, {
        roles: ["superadmin"],
      });
      message.success(t("Super admin role assigned successfully"));
      setSuperAdminModalOpen(false);
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || t("Failed to assign role"));
    } finally {
      setAssigningRole(false);
    }
  };

  const submitUser = async () => {
    if (!selectedClient?._id) return;
    try {
      const values = await userForm.validateFields();
      setCreatingUser(true);
      const contractStart = values.contractStart
        ? values.contractStart.toISOString?.()
        : undefined;
      const contractEnd = values.contractEnd
        ? values.contractEnd.toISOString?.()
        : undefined;
      await axios.post(`${BASE_API_URL}/auth/signup`, {
        email: values.email,
        password: values.password,
        clientId: selectedClient._id,
        color: "#1890ff",
        roles: [values.role],
        firstName: values.firstName,
        lastName: values.lastName,
        workingPatternId: values.workingPatternId,
        gender: values.gender,
        contractStart,
        contractEnd,
        locationId: values.locationId,
      });
      message.success(t("User created"));
      setUserModalOpen(false);
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
      message.error(
        error?.response?.data?.message || t("Unable to create user")
      );
    } finally {
      setCreatingUser(false);
    }
  };

  const columns = useMemo<ColumnsType<ClientRecord>>(
    () => [
      { title: t("Name"), dataIndex: "name", key: "name" },
      { title: t("Client Name"), dataIndex: "clientName", key: "clientName" },
      {
        title: t("Max users"),
        dataIndex: "maxUserNumber",
        key: "maxUserNumber",
        width: 120,
      },
      {
        title: t("Currency"),
        dataIndex: "currency",
        key: "currency",
        render: (value: string, record: ClientRecord) =>
          value || record.currencyIcon || "-",
        width: 140,
      },
      {
        title: t("Enabled features"),
        key: "features",
        width: 150,
        render: (_: any, record: ClientRecord) => {
          const count = record.settings?.enabledFeatures?.length || 0;
          return (
            <Tag color={count ? "green" : "default"}>
              {count} {t("Features")}
            </Tag>
          );
        },
      },
      {
        title: "ID",
        dataIndex: "_id",
        key: "_id",
      },
      {
        title: t("Actions"),
        key: "actions",
        width: 220,
        render: (_, record) => (
          <Space>
            <Button type="link" onClick={() => openUserModal(record)}>
              {t("Add user")}
            </Button>
            <Button type="link" onClick={() => openSuperAdminModal(record)}>
              {t("Super Admin")}
            </Button>
          </Space>
        ),
      },
    ],
    [openUserModal, openSuperAdminModal, t]
  );

  if (!isSuperAdmin(userData?.roles)) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t("This section is available only to super admins.")}
      />
    );
  }

  return (
    <div className="settingsTab">
      <Row gutter={16}>
        <Col xs={24} lg={8}>
          <Card>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>
                <Paragraph style={{ marginBottom: 4 }}>
                  <Text strong>{t("Create a new client")}</Text>
                </Paragraph>
                <Text type="secondary">
                  {t(
                    "Fill in the basic client info. Default working patterns are created automatically."
                  )}
                </Text>
              </div>
              <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                initialValues={{ maxUserNumber: 0 }}
              >
                <Form.Item
                  label={t("Name")}
                  name="name"
                  rules={[
                    { required: true, message: t("Please enter client name") },
                  ]}
                >
                  <Input placeholder={t("Internal name")} />
                </Form.Item>
                <Form.Item
                  label={t("Client Name")}
                  name="clientName"
                  rules={[
                    { required: true, message: t("Please enter display name") },
                  ]}
                >
                  <Input placeholder={t("Display name")} />
                </Form.Item>
                <Form.Item
                  label={t("Max users")}
                  name="maxUserNumber"
                  rules={[
                    { required: true, message: t("Please enter max users") },
                  ]}
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item label={t("Currency")} name="currency">
                  <Input placeholder={t("e.g. EUR")} />
                </Form.Item>
                <Form.Item label={t("Currency icon")} name="currencyIcon">
                  <Input placeholder={t("e.g. €")} />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={creating}
                    block
                  >
                    {t("Create client")}
                  </Button>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          <Card title={t("Existing clients")}>
            <Table
              rowKey="_id"
              loading={loading}
              dataSource={clients}
              columns={columns}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        open={userModalOpen}
        title={
          selectedClient
            ? t("Add user for {{client}}", { client: selectedClient.name })
            : t("Add user")
        }
        onCancel={() => setUserModalOpen(false)}
        onOk={submitUser}
        confirmLoading={creatingUser}
        okText={t("Save")}
        className="standardHeader standardFooter"
      >
        {!workingPatterns.length && (
          <Alert
            type="warning"
            showIcon
            message={t("No working patterns found for this client.")}
            style={{ marginBottom: 12 }}
          />
        )}
        <Form layout="vertical" form={userForm}>
          <Form.Item
            label={t("First name")}
            name="firstName"
            rules={[{ required: true, message: t("Please enter first name") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("Last name")}
            name="lastName"
            rules={[{ required: true, message: t("Please enter last name") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, type: "email", message: t("Please enter email") },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("Password")}
            name="password"
            rules={[
              { required: true, message: t("Please enter temporary password") },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            label={t("Role")}
            name="role"
            rules={[{ required: true, message: t("Please choose role") }]}
          >
            <Select>
              <Select.Option value="user">{t("Employee")}</Select.Option>
              <Select.Option value="moderator">{t("Moderator")}</Select.Option>
              <Select.Option value="admin">{t("Admin")}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label={t("Working pattern")}
            name="workingPatternId"
            rules={[{ required: true, message: t("Please select contract") }]}
          >
            <Select
              placeholder={t("Select working pattern")}
              options={workingPatterns.map((wp) => ({
                value: wp._id,
                label: wp.contractName || wp.type || wp._id,
              }))}
            />
          </Form.Item>
          <Form.Item
            label={t("Gender")}
            name="gender"
            rules={[{ required: true, message: t("Please select gender") }]}
          >
            <Select>
              <Select.Option value="male">{t("Male")}</Select.Option>
              <Select.Option value="female">{t("Female")}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label={t("Contract start")}
            name="contractStart"
            rules={[
              { required: true, message: t("Please pick start date") },
            ]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label={t("Contract end")} name="contractEnd">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label={t("Location")} name="locationId">
            <Select
              placeholder={t("Select location (optional)")}
              options={locations.map((loc) => ({
                value: loc._id,
                label: loc.name,
              }))}
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={superAdminModalOpen}
        title={
          selectedClient
            ? t("Assign Super Admin — {{client}}", { client: selectedClient.name })
            : t("Assign Super Admin")
        }
        onCancel={() => setSuperAdminModalOpen(false)}
        onOk={assignSuperAdmin}
        confirmLoading={assigningRole}
        okText={t("Assign Super Admin")}
        className="standardHeader standardFooter"
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="secondary">
            {t("Select an employee to assign the Super Admin role. This will replace their current roles.")}
          </Text>
          <Select
            style={{ width: "100%" }}
            placeholder={t("Select employee")}
            loading={loadingUsers}
            value={selectedUserId}
            onChange={setSelectedUserId}
            showSearch
            filterOption={(input, option) =>
              String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={clientUsers.map((u) => {
              const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ");
              const label = fullName || u.username || u.email || u._id;
              const currentRoles = (u.roles || [])
                .map((r: any) => (typeof r === "string" ? r : r?.name))
                .filter(Boolean)
                .join(", ");
              return {
                value: u._id,
                label: currentRoles ? `${label} (${currentRoles})` : label,
              };
            })}
          />
          {selectedUserId && (
            <Alert
              type="warning"
              showIcon
              message={t("This will set the selected user's role to Super Admin only.")}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default Clients;
